import * as React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
} from '@mui/material';
import { ErrorList, FileCard } from '../components';
import { Upload } from '@phosphor-icons/react';
import { FieldProp } from 'react-typed-form';
import { Api, useApiCallable } from '../api';
import config from '../config';

const BUSY = '_busy';

type AcceptedType = 'VIDEO_TYPE' | 'AUDIO_TYPE';

const getAcceptedTypes = (acceptedTypes?: AcceptedType[]) => {
  let accept: string[] = [];
  if (!acceptedTypes) {
    return [
      ...config.ACCEPTED_MEDIA_TYPES.VIDEO_TYPE,
      ...config.ACCEPTED_MEDIA_TYPES.AUDIO_TYPE,
    ];
  }
  if (acceptedTypes.includes('VIDEO_TYPE')) {
    accept = [...accept, ...config.ACCEPTED_MEDIA_TYPES.VIDEO_TYPE];
  }
  if (acceptedTypes.includes('AUDIO_TYPE')) {
    accept = [...accept, ...config.ACCEPTED_MEDIA_TYPES.AUDIO_TYPE];
  }
  return accept;
};

const getButtonLabel = (acceptedTypes?: AcceptedType[]) => {
  if (!acceptedTypes || acceptedTypes.length > 1) {
    return 'Upload Media';
  }
  if (acceptedTypes[0] === 'VIDEO_TYPE') {
    return 'Upload Video';
  }
  if (acceptedTypes[0] === 'AUDIO_TYPE') {
    return 'Upload Audio';
  }
};

type Props = {
  field: FieldProp<string | typeof BUSY | null>;
  label?: string;
  existingAsset?: Api.MediaUpload.BaseView | null;
  acceptedTypes?: AcceptedType[];
  required?: boolean;
  helperText?: string;
  aspectRatio?: string;
};

export default function FieldLargeMediaUpload({
  field,
  label,
  existingAsset,
  acceptedTypes,
  required,
  helperText,
}: Props) {
  const [mimeType, setMimeType] = React.useState<undefined | string>(
    existingAsset?.mimeType ?? undefined,
  );
  const [uploadingFile, setUploadingFile] = React.useState<File | null>(null);
  const callApi = useApiCallable();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    field.handleValueChange(BUSY);
    setUploadingFile(file);

    try {
      const presignResponse = await callApi<
        {
          url: string;
          method: string;
          key: string;
          storedFileName: string;
        },
        object
      >('/media-uploads/presign', {
        method: 'POST',
        jsonBody: {
          fileName: file.name,
          mimeType: file.type,
        },
      });

      if (!presignResponse) {
        field.handleValueChange(null);
        return;
      }

      const uploadResponse = await fetch(presignResponse.data.url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        field.handleValueChange(null);
        return;
      }

      const linkResponse = await callApi<Api.MediaUpload.BaseView, object>(
        '/media-uploads/link-s3',
        {
          method: 'POST',
          jsonBody: {
            storedFileName: presignResponse.data.storedFileName,
            originalFileName: file.name,
            mimeType: file.type,
          },
        },
      );

      if (linkResponse) {
        field.handleValueChange(`/media-uploads/${linkResponse.data.id}`);
        setMimeType(linkResponse.data.mimeType);
      } else {
        field.handleValueChange(null);
      }
    } catch (error) {
      field.handleValueChange(null);
      setUploadingFile(null);
    }
  };

  return (
    <>
      <FormControl>
        <Stack spacing={1}>
          <FormLabel required={required}>
            {label ?? field.label ?? 'Media'}
          </FormLabel>
          <Stack alignItems="flex-start" spacing={1}>
            <Box>
              {field.value ? (
                <FileCard
                  isLoading={field.value === BUSY}
                  mimeType={mimeType}
                  filename={
                    field.value === BUSY
                      ? uploadingFile?.name
                      : existingAsset?.videoEncodedHeights?.length
                        ? uploadingFile?.name || existingAsset?.id
                        : 'Processing...'
                  }
                  onDelete={() => {
                    field.handleValueChange(null);
                    setUploadingFile(null);
                  }}
                  downloadHref={
                    field.value === BUSY ||
                    !existingAsset?.videoEncodedHeights?.length ||
                    !existingAsset?.videoUrlTemplate
                      ? null
                      : existingAsset.videoUrlTemplate.replace(
                          '__ENCODING__',
                          existingAsset.videoEncodedHeights[0].toString(),
                        )
                  }
                />
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  component="label"
                >
                  {getButtonLabel(acceptedTypes)}
                  <input
                    type="file"
                    hidden
                    accept={getAcceptedTypes(acceptedTypes).join(',')}
                    onChange={handleFileChange}
                  />
                </Button>
              )}
            </Box>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
            <ErrorList errors={field.lastErrorList} />
          </Stack>
        </Stack>
      </FormControl>
    </>
  );
}
